import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled from 'styled-components';

import FloatingActionButton from '@ui/buttons/FloatingActionButton.tsx';

import { useCSSColors } from '../../../../src/hooks/useCSSColors.ts';

const PanControlsWrapper = styled.div`
  position: absolute;
  display: flex;
  gap: 12px;
  max-width: 77vw;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 5px;

  & > button:focus-visible {
    outline-offset: -2px;
  }
  inset-inline-end: 55px;
`;

const MapPanFab = styled(FloatingActionButton)`
  background: ${({ $active }) => ($active ? 'var(--color-primaryButton)' : 'var(--color-background)')};
`;

const FloatingActionButtonRotated = styled(FloatingActionButton)`
  // This is a hack solution, but at this time SVGs without fill are not available from content
  transform: rotate(90deg);
`;

const MapPanControlsMobile = ({ bus, T, delta = 150, app }) => {
  const colors = useCSSColors();
  const [showPanControls, setShowPanControls] = useState(false);
  const [isMobileReflow, setisMobileReflow] = useState(app.env.isMobileReflow());
  const panControlsRef = useRef(null);

  // Key handler to allow horizontal (left/right) arrow navigation among visible buttons
  const onPanKeyDown = useCallback(e => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') {
      return;
    }
    const container = panControlsRef.current;
    if (!container) {
      return;
    }
    // Include the toggle button (MapPanFab) as first/last in the logical order by querying preceding sibling
    const toggleButton = document.querySelector('[data-testid="MapPanFab"]');
    const panelButtons = Array.from(container.querySelectorAll('button'));
    // Build ordered list: toggle + panel buttons
    const ordered = toggleButton ? [toggleButton, ...panelButtons] : panelButtons;
    if (!ordered.length) {
      return;
    }
    const current = document.activeElement;
    const idx = ordered.indexOf(current);
    // If focus is not within list and user presses an arrow, move focus to first panel button
    if (idx === -1) {
      e.preventDefault();
      if (panelButtons[0]) {
        panelButtons[0].focus();
      }
      return;
    }
    e.preventDefault();
    const delta = e.key === 'ArrowRight' ? 1 : -1;
    const nextIdx = (idx + delta + ordered.length) % ordered.length;
    ordered[nextIdx].focus();
  }, []);

  useEffect(() => {
    const unsub = bus.on('app/reset', () => setShowPanControls(false));
    return unsub;
  }, []);
  useEffect(() => {
    const unsub = bus.on('env/resize', async () => {
      if (isMobileReflow !== app.env.isMobileReflow()) {
        setisMobileReflow(app.env.isMobileReflow());
      }
    });
    return unsub;
  }, [isMobileReflow]);
  const togglePanControls = async () => {
    setShowPanControls(previouslyShowing => {
      bus.send('mapPanControls/panControlsOpen', {
        shouldOpen: !previouslyShowing,
      });
      return !previouslyShowing;
    });
  };

  const mapMoveAction = move => {
    switch (move) {
      case 'panUp':
        bus.send('map/pan', { offset: [0, -1 * delta], relSpeed: 2, userInitiated: true });
        bus.send('appInsights/log', {
          name: 'mapPanControls/panUp',
        });
        break;
      case 'panDown':
        bus.send('map/pan', { offset: [0, delta], relSpeed: 2, userInitiated: true });
        bus.send('appInsights/log', {
          name: 'mapPanControls/panDown',
        });
        break;
      case 'panLeft':
        bus.send('map/pan', { offset: [-1 * delta, 0], relSpeed: 2, userInitiated: true });
        bus.send('appInsights/log', {
          name: 'mapPanControls/panLeft',
        });
        break;
      case 'panRight':
        bus.send('map/pan', { offset: [delta, 0], relSpeed: 2, userInitiated: true });
        bus.send('appInsights/log', {
          name: 'mapPanControls/panRight',
        });
    }
  };
  const onZoomIn = () => bus.send('map/zoomIn', { userInitiated: true });
  const onZoomOut = () => bus.send('map/zoomOut', { userInitiated: true });

  // Keep internal state in sync with bus (so other widgets can close)
  useEffect(
    () =>
      bus.on('mapPanControls/panControlsOpen', ({ shouldOpen }) => {
        setShowPanControls(!!shouldOpen);
      }),
    [],
  );

  return (
    <>
      <MapPanFab
        data-testid="MapPanFab"
        iconName="move.note"
        $active={showPanControls}
        iconColor={showPanControls ? colors.primaryButtonText : colors.secondaryText}
        onClick={togglePanControls}
        ariaLabel={T('mapPanControls:Show Panning Controls')}
        aria-pressed={showPanControls}
        /* Only provide aria-controls when the controlled element exists (WCAG valid ARIA) */
        aria-controls={showPanControls ? 'mapPanControls' : undefined}
        aria-expanded={showPanControls}
        // Allow arrow key navigation to move focus from inside panel back to the toggle
        onKeyDown={e => {
          if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && showPanControls) {
            // Panel already open: ArrowLeft -> last button, ArrowRight -> first button
            e.preventDefault();
            const buttons = panControlsRef.current?.querySelectorAll('button');
            if (buttons && buttons.length) {
              const target = e.key === 'ArrowLeft' ? buttons[buttons.length - 1] : buttons[0];
              target.focus();
            }
          }
        }}
      />
      {showPanControls && (
        <PanControlsWrapper
          id={'mapPanControls'}
          data-testid="mapPanControls"
          $isMobileReflow={isMobileReflow}
          ref={panControlsRef}
          role="toolbar"
          aria-label={T('mapPanControls:Map pan and zoom controls')}
          aria-orientation="horizontal"
          onKeyDown={onPanKeyDown}
          tabIndex={-1}
        >
          <FloatingActionButton
            data-testid="mobilePanUp"
            iconName="arrow.up"
            ariaLabel={T('mapPanControls:Pan Map Up')}
            onClick={() => mapMoveAction('panUp')}
            tabIndex={-1}
            style={{ flexShrink: 0 }}
          />
          <FloatingActionButton
            data-testid="mobilePanDown"
            iconName="arrow.down"
            ariaLabel={T('mapPanControls:Pan Map Down')}
            onClick={() => mapMoveAction('panDown')}
            tabIndex={-1}
            style={{ flexShrink: 0 }}
          />
          <FloatingActionButtonRotated
            data-testid="mobilePanLeft"
            iconName="arrow.down"
            ariaLabel={T('mapPanControls:Pan Map Left')}
            onClick={() => mapMoveAction('panLeft')}
            tabIndex={-1}
            style={{ flexShrink: 0 }}
          />
          <FloatingActionButtonRotated
            data-testid="mobilePanRight"
            iconName="arrow.up"
            ariaLabel={T('mapPanControls:Pan Map Right')}
            onClick={() => mapMoveAction('panRight')}
            tabIndex={-1}
            style={{ flexShrink: 0 }}
          />
          <FloatingActionButton
            data-testid="mobileZoomIn"
            iconName="zoom.in"
            ariaLabel={T('mapPanControls:Zoom In')}
            onClick={onZoomIn}
            tabIndex={-1}
            style={{ flexShrink: 0 }}
          />
          <FloatingActionButton
            data-testid="mobileZoomOut"
            iconName="zoom.out"
            ariaLabel={T('mapPanControls:Zoom Out')}
            onClick={onZoomOut}
            tabIndex={-1}
            style={{ flexShrink: 0 }}
          />
        </PanControlsWrapper>
      )}
    </>
  );
};

export default MapPanControlsMobile;
